import { useEffect, useState } from 'react'
import {
  Box, Button, CircularProgress, Dialog, IconButton, Stack, Typography,
} from '@mui/material'
import CloseIcon from '@mui/icons-material/Close'
import DownloadOutlinedIcon from '@mui/icons-material/DownloadOutlined'
import type { VendorDocument } from '@cmms/api-client'

interface Props {
  open: boolean
  doc: VendorDocument | null
  /**
   * Public URL the browser can load directly (e.g. MinIO public-read URL).
   * Preferred when present — skips the blob round-trip through the API.
   * Mobile uses the same path; this aligns web with that.
   */
  directUrl?: string | null
  /**
   * Fallback when no directUrl exists (auth-gated buckets, legacy code).
   * Streams the file as a Blob, then renders via object URL.
   */
  fetchBlob?: () => Promise<{ data: Blob; headers?: any }>
  onClose: () => void
}

const IMAGE_TYPES = new Set(['image/jpeg', 'image/png', 'image/webp', 'image/gif', 'image/svg+xml'])
const PDF_TYPE = 'application/pdf'
const TEXT_TYPES = new Set(['text/plain', 'text/csv', 'text/html', 'application/json'])

export function DocumentViewer({ open, doc, directUrl, fetchBlob, onClose }: Props) {
  const [loading, setLoading] = useState(false)
  const [blobUrl, setBlobUrl] = useState<string | null>(null)
  const [textContent, setTextContent] = useState<string | null>(null)
  const [error, setError] = useState(false)

  // Always materialize the file through a same-origin blob URL — both for
  // display AND download. Two reasons we can't just use the MinIO URL:
  //   1. <a download="..."> is silently IGNORED cross-origin, so clicking
  //      "Download" just navigates instead of saving the file.
  //   2. PDFs in <iframe> can be blocked by X-Frame-Options or CSP on the
  //      object store. The blob URL is same-origin so neither applies.
  // We keep the auth-gated `fetchBlob` path as the preferred source; the
  // public URL is the fallback when no API helper was passed.
  useEffect(() => {
    if (!open || !doc) return
    setLoading(true)
    setError(false)
    setBlobUrl(null)
    setTextContent(null)

    const ct = doc.content_type ?? ''
    const isText = TEXT_TYPES.has(ct) || ct.startsWith('text/')

    const fetcher = fetchBlob
      ? fetchBlob
      : directUrl
        ? () => fetch(directUrl).then(async (r) => {
            if (!r.ok) throw new Error(`HTTP ${r.status}`)
            return { data: await r.blob() }
          })
        : null
    if (!fetcher) { setLoading(false); setError(true); return }

    let revokeUrl: string | null = null
    let cancelled = false
    fetcher()
      .then(async (res) => {
        if (cancelled) return
        const blob = res.data instanceof Blob ? res.data : new Blob([res.data])
        if (isText) {
          const text = await blob.text()
          if (!cancelled) setTextContent(text)
        } else {
          const url = URL.createObjectURL(blob)
          revokeUrl = url
          if (!cancelled) setBlobUrl(url)
        }
      })
      .catch(() => { if (!cancelled) setError(true) })
      .finally(() => { if (!cancelled) setLoading(false) })

    return () => {
      cancelled = true
      if (revokeUrl) URL.revokeObjectURL(revokeUrl)
    }
  }, [open, doc?.id, directUrl])

  function handleDownload() {
    if (!doc) return
    // `blobUrl` is the same-origin object URL produced above, so the
    // browser will honor the `download` attribute. (A cross-origin
    // direct URL here would just navigate — the attribute is dropped.)
    const url = blobUrl
      ?? (textContent != null
        ? URL.createObjectURL(new Blob([textContent], { type: doc.content_type ?? 'text/plain' }))
        : null)
    if (!url) return
    const a = document.createElement('a')
    a.href = url
    a.download = doc.file_name ?? 'file'
    document.body.appendChild(a)
    a.click()
    document.body.removeChild(a)
    if (url !== blobUrl) setTimeout(() => URL.revokeObjectURL(url), 1000)
  }

  const ct = doc?.content_type ?? ''
  const isImage = IMAGE_TYPES.has(ct)
  const isPdf = ct === PDF_TYPE
  const isText = TEXT_TYPES.has(ct) || ct.startsWith('text/')

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth
      PaperProps={{ sx: { borderRadius: 2.5, height: '85vh', display: 'flex', flexDirection: 'column', overflow: 'hidden' } }}>

      {/* Header */}
      <Stack direction="row" alignItems="center" sx={{ px: 3, py: 1.5, borderBottom: '1px solid', borderColor: 'divider', flexShrink: 0 }}>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography sx={{ fontSize: 14, fontWeight: 700 }} noWrap>{doc?.file_name}</Typography>
          <Typography sx={{ fontSize: 11.5, color: 'text.secondary' }}>
            {[ct, doc ? formatSize(doc.file_size) : ''].filter(Boolean).join(' · ')}
          </Typography>
        </Box>
        <Stack direction="row" spacing={0.5}>
          <Button size="small" startIcon={<DownloadOutlinedIcon sx={{ fontSize: 15 }} />}
            onClick={handleDownload}
            sx={{ fontSize: 12, fontWeight: 600, textTransform: 'none' }}>
            Download
          </Button>
          <IconButton size="small" onClick={onClose}><CloseIcon sx={{ fontSize: 18 }} /></IconButton>
        </Stack>
      </Stack>

      {/* Content */}
      <Box sx={{ flex: 1, overflow: 'auto', display: 'flex', alignItems: 'center', justifyContent: 'center',
        bgcolor: (t) => t.palette.mode === 'light' ? '#F4F5F7' : '#0F172A' }}>

        {loading && <CircularProgress size={32} />}

        {error && (
          <Typography sx={{ color: 'text.secondary', fontSize: 14 }}>Failed to load document</Typography>
        )}

        {!loading && !error && isPdf && blobUrl && (
          <iframe src={blobUrl} title={doc?.file_name}
            style={{ width: '100%', height: '100%', border: 'none' }} />
        )}

        {!loading && !error && isImage && blobUrl && (
          <Box sx={{ p: 3, maxHeight: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <img src={blobUrl} alt={doc?.file_name}
              style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain', borderRadius: 8 }} />
          </Box>
        )}

        {!loading && !error && isText && textContent !== null && (
          <Box sx={{ width: '100%', height: '100%', overflow: 'auto', p: 3 }}>
            <Box sx={{
              fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
              fontSize: 12.5, lineHeight: 1.7, whiteSpace: 'pre-wrap', wordBreak: 'break-word',
              color: 'text.primary',
              bgcolor: 'background.paper', borderRadius: 2, p: 3,
              border: '1px solid', borderColor: 'divider',
              minHeight: '100%',
            }}>
              {textContent}
            </Box>
          </Box>
        )}

        {/* Fallback for unsupported types (Word, Excel, etc.) */}
        {!loading && !error && !isPdf && !isImage && !isText && blobUrl && (
          <Stack alignItems="center" spacing={2} sx={{ py: 6 }}>
            <Typography sx={{ fontSize: 48, opacity: 0.3 }}>📄</Typography>
            <Typography sx={{ fontSize: 15, fontWeight: 600, color: 'text.secondary' }}>
              Preview not available for this file type
            </Typography>
            <Typography sx={{ fontSize: 13, color: 'text.disabled' }}>{doc?.file_name} · {ct}</Typography>
            <Button variant="contained" startIcon={<DownloadOutlinedIcon />} onClick={handleDownload}
              sx={{ borderRadius: 2, textTransform: 'none' }}>
              Download file
            </Button>
          </Stack>
        )}
      </Box>
    </Dialog>
  )
}

function formatSize(bytes: number | null | undefined) {
  if (bytes == null || bytes <= 0) return ''
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}
